/**
 * Imported (TravelBooster) records — the one place that knows the id band.
 *
 * The database is split in two. `taytan_pegasus` holds everything the
 * application creates; `taytan_pegasus_import` holds records imported from
 * TravelBooster. Inside the app database each `public.<table>` is a UNION ALL
 * view over `app.<table>` (native) and `tb.<table>` (a postgres_fdw foreign
 * table), and an INSTEAD OF trigger refuses any UPDATE or DELETE of a row in
 * the imported band — see data/scripts/sql/split_app_db_with_fdw_views.sql.
 *
 * An id is therefore self-describing: it says which database owns the row.
 *
 * WHAT THE TRIGGER DOES NOT COVER: inserting a *child* row that points at an
 * imported parent. `INSERT INTO services` with a `travelfileId` in the imported
 * band succeeds and creates a native row hanging off an imported one. For
 * `create` and `issue`, therefore, the UI guard built on this module is the only
 * thing standing in the way — there is no database backstop behind it.
 *
 * This file deliberately has NO imports. It is consumed from both halves of the
 * codebase — `src/services/db/**` on the server and `src/permissions/**` in the
 * browser — and anything pulled in here would be dragged into both bundles.
 */

/**
 * The floor of the imported id band.
 *
 * Imported rows are numbered from 1,000,000,000 up; the app allocates from 1.
 * Set by data/scripts/python/migrations/import_to_taytan_galor.sh, which resets
 * every import-owned sequence to this value on --full. The two must agree.
 */
export const TB_ID_FLOOR = 1_000_000_000;

/**
 * Did this record come from TravelBooster?
 *
 * Null, undefined and unparseable ids are NOT imported — a record with no id
 * yet is one being created, which is always native.
 */
export const isImportedId = (id: string | number | null | undefined): boolean => {
    if (id === null || id === undefined || id === '') return false;
    const n = Number(id);
    return Number.isFinite(n) && n >= TB_ID_FLOOR;
};

/**
 * The single sentence shown wherever an imported record disables a control.
 *
 * One string so the wording is changed here and nowhere else.
 */
export const IMPORTED_RECORD_TOOLTIP =
    'Imported from TravelBooster — read-only here. Make changes in TravelBooster.';
